"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Select, Checkbox, Collapse, Drawer, Grid, Modal } from "antd";
import {
  IconFilter,
  IconX,
  IconSearch,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconArrowsDownUp,
  IconArrowNarrowLeft,
} from "@/lib/icons";
import Container from "../container";
import styles from "./listing.module.css";
import { ENDPOINTS, getApiV2Url } from "@/constants/endpoint";
import { api } from "@/lib/api";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { formatCityName } from "@/lib/formatCityName";
import { sortEventsList } from "@/lib/listingQuery";
import { usePathname, useRouter } from "next/navigation";
import Event from "../Event/event";
import ContestCard from "../Contest/ContestCard";
import ArtistCard from "../Artist/ArtisCard";
import OrganizerListingCard from "../Organizer/OrganizerListingCard";

const ARTIST_SEARCH_MIN_LEN = 3;

const pickArtistImage = (pic) => {
  if (!pic) return undefined;
  if (typeof pic === "string") return pic;
  const file = Array.isArray(pic) ? pic[0] : pic;
  if (!file) return undefined;
  if (typeof file === "string") return file;
  return (
    file.md ||
    file.sd ||
    file.url ||
    file.g ||
    file.lg ||
    file.original ||
    file.src ||
    undefined
  );
};
import { trackFilterEvents } from "@/lib/analytics";

/** Map UI / CMS filter tokens → Laravel public-event filter values. */
function normalizeFilterToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[₹$€]/g, "")
    .replace(/\brs\.?\b/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function mapDateFilterValues(values = []) {
  const map = {
    today: "today",
    tomorrow: "tomorrow",
    tommorrow: "tomorrow",
    "this weekend": "this weekend",
    "this week": "this week",
    "this month": "this month",
    "7 days": "7 days",
    "next 7 days": "7 days",
    "1 month": "1 month",
    "next 1 month": "1 month",
    "3 months": "3 months",
    "next 3 months": "3 months",
    "6 months": "6 months",
    "next 6 months": "6 months",
  };
  return values
    .map((v) => map[normalizeFilterToken(v)] || String(v).trim())
    .filter(Boolean);
}

function mapPriceFilterValues(values = []) {
  const map = {
    free: "Free",
    "under 500": "0 - 500",
    "0 500": "0 - 500",
    "0 to 500": "0 - 500",
    "500 2000": "501 - 2000",
    "501 2000": "501 - 2000",
    "500 to 2000": "501 - 2000",
    "above 2000": "above 2000",
  };
  return values
    .map((v) => {
      const key = normalizeFilterToken(v);
      return map[key] || String(v).trim();
    })
    .filter(Boolean);
}

function mapSortFilterValue(value) {
  if (!value) return "";
  const key = normalizeFilterToken(value).replace(/\s+/g, "_");
  const map = {
    latest: "latest",
    date: "latest",
    by_date: "latest",
    event_date_asc: "latest",
    event_date: "latest",
    popularity: "popularity",
    price_low_high: "price_low_high",
    by_lowest_price: "price_low_high",
    lowest_price: "price_low_high",
    price_low_to_high: "price_low_high",
    price_high_low: "price_high_low",
    by_highest_price: "price_high_low",
    highest_price: "price_high_low",
    price_high_to_low: "price_high_low",
  };
  return map[key] || value;
}
/** Windowed page-number list with "…" gaps, e.g. [1, "...", 4, 5, 6, "...", 12]. */
function getPageNumbers(current, total) {
  const delta = 2;
  const range = [];
  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }
  const withDots = [];
  let last;
  range.forEach((i) => {
    if (last !== undefined) {
      if (i - last === 2) withDots.push(last + 1);
      else if (i - last !== 1) withDots.push("...");
    }
    withDots.push(i);
    last = i;
  });
  return withDots;
}

function Pagination({ page, totalPages, disabled, onChange }) {
  if (totalPages <= 1) return null;
  const pages = getPageNumbers(page, totalPages);

  return (
    <nav className={styles.pagination} aria-label="Pagination">
      <button
        type="button"
        className={styles.pageNavBtn}
        onClick={() => onChange(page - 1)}
        disabled={disabled || page === 1}
        aria-label="Previous page"
      >
        <IconChevronLeft size={16} />
        <span>Prev</span>
      </button>

      <div className={styles.pageNumbers}>
        {pages.map((p, idx) =>
          p === "..." ? (
            <span key={`dots-${idx}`} className={styles.pageDots}>
              …
            </span>
          ) : (
            <button
              type="button"
              key={p}
              className={`${styles.pageNumBtn} ${p === page ? styles.pageNumBtnActive : ""}`}
              onClick={() => onChange(p)}
              disabled={disabled}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          ),
        )}
      </div>

      <button
        type="button"
        className={styles.pageNavBtn}
        onClick={() => onChange(page + 1)}
        disabled={disabled || page === totalPages}
        aria-label="Next page"
      >
        <span>Next</span>
        <IconChevronRight size={16} />
      </button>
    </nav>
  );
}

function SkeletonCard() {
  return (
    <div className={styles.skeletonCard}>
      <div className={styles.skeletonImg} />
      <div className={styles.skeletonBody}>
        <div className={styles.skeletonLine} />
        <div className={`${styles.skeletonLine} ${styles.short}`} />
        <div className={styles.skeletonLine} />
      </div>
    </div>
  );
}

function FilterGroup({ label, options, active, onToggle }) {
  const items = [
    {
      key: label,
      label: (
        <div className={styles.filterAccordionTitle}>
          <span>{label}</span>
        </div>
      ),
      children: (
        <div className={styles.filterCheckboxList}>
          {options.map((opt) => {
            const val = typeof opt === "object" ? opt.value : opt;
            const displayLabel = typeof opt === "object" ? opt.label : opt;

            return (
              <div
                key={val}
                className={styles.filterCheckboxItem}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={() => onToggle(val)}
              >
                <Checkbox checked={active.includes(val)} />
                <span
                  style={{
                    userSelect: "none",
                    color: active.includes(val) ? "#0B192C" : "#556685",
                    fontWeight: active.includes(val) ? 500 : 400,
                    transition: "color 0.2s ease",
                  }}
                >
                  {displayLabel}
                </span>
              </div>
            );
          })}
        </div>
      ),
    },
  ];

  return (
    <Collapse
      bordered={false}
      defaultActiveKey={[label]}
      expandIcon={({ isActive }) => (
        <IconChevronDown
          size={16}
          color="#556685"
          stroke={3}
          style={{
            transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.2s",
          }}
        />
      )}
      className={styles.filterAccordion}
      items={items}
    />
  );
}

export default function Listing({
  initialData = [],
  initialTotalPages = 1,
  initialIsFallback = false,
  config = {},
  city: initialCity,
  type: propType,
  endpoint: propEndpoint,
  renderCard,
  redirectUrl: propRedirectUrl,
}) {
  const filterBarRef = useRef(null);

  useEffect(() => {
    // We need to poll or handle mutations if the element mounts late,
    // but a simpler way is to just attach it to the window or a wrapper, 
    // or use a MutationObserver. 
    // Actually, attaching the event listener when the ref changes is best done via a callback ref.
  }, []);

  const setFilterBarRef = useCallback((el) => {
    if (filterBarRef.current) {
      // cleanup previous
      filterBarRef.current.removeEventListener("wheel", filterBarRef.current._handleWheel);
    }
    if (el) {
      const handleWheel = (e) => {
        if (e.deltaY !== 0) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      };
      el._handleWheel = handleWheel;
      el.addEventListener("wheel", handleWheel, { passive: false });
    }
    filterBarRef.current = el;
  }, []);
  // Handle nested config data (e.g. data["1"].data) from CMS
  const nestedConfig = config?.["1"]?.data || {};
  const safeConfig = { ...config, ...nestedConfig };

  const path = usePathname() || "";
  const router = useRouter();
  const splitPath = path.split("/");

  const isContestFromUrl =
    splitPath.includes("contests") ||
    splitPath.includes("contest") ||
    path.endsWith("/contests") ||
    path.endsWith("/contest");

  /* ── derive type & endpoint ── */
  const typeRaw =
    propType ||
    safeConfig.listing_type ||
    safeConfig.resource_type ||
    safeConfig.group ||
    safeConfig.type ||
    (isContestFromUrl ? "contest" : "event");
  const type = String(typeRaw).toLowerCase();
  const isContestListing =
    type === "contest" || type === "contests" || isContestFromUrl;

  const isArtistListing =
    type === "artist" ||
    type === "artists" ||
    type === "artistlistingsection" ||
    type === "artistlisting" ||
    safeConfig.listing_type === "artist" ||
    safeConfig.listing_type === "artists" ||
    splitPath.includes("artists") ||
    splitPath.includes("artist");

  const isOrganizerListing =
    type === "organiser" ||
    type === "organizer" ||
    type === "organiserlistingsection" ||
    type === "organizerlistingsection" ||
    safeConfig.listing_type === "organiser" ||
    safeConfig.listing_type === "organizer" ||
    // "/organizers" is the listing route; "/organizer/<slug>" is a single
    // organizer's detail page and must NOT match here.
    splitPath.includes("organizers");

  const isThirdPartyListing =
    Boolean(safeConfig.is_third_party) ||
    safeConfig.is_third_party === "true" ||
    type === "third_party" ||
    type === "third_party_events" ||
    type === "third_party_event" ||
    type === "thirdpartyevent" ||
    type === "thirdparty" ||
    safeConfig.listing_type === "third_party" ||
    safeConfig.listing_type === "third_party_events" ||
    safeConfig.listing_type === "third_party_event" ||
    safeConfig.listing_type === "thirdparty" ||
    propEndpoint === ENDPOINTS.THIRD_PART_EVENT ||
    safeConfig.endpoint === ENDPOINTS.THIRD_PART_EVENT ||
    propEndpoint === "EventManagement/public-third-party-event" ||
    safeConfig.endpoint === "EventManagement/public-third-party-event";

  const endpoint =
    propEndpoint ||
    safeConfig.endpoint ||
    (isThirdPartyListing
      ? ENDPOINTS.THIRD_PART_EVENT
      : isContestListing
        ? ENDPOINTS.GET_CONTESTS
        : isArtistListing
          ? ENDPOINTS.ARTIST
          : isOrganizerListing
            ? ENDPOINTS.ORGANIZER_LIST
            : ENDPOINTS.HOME);
  const redirectUrl = propRedirectUrl || safeConfig.redirectUrl;
  const city = initialCity || safeConfig.city || "ahmedabad";
  const category = splitPath[2]?.toUpperCase();

  // Category pages (listing_type: "category") send the page's fixed
  // category as a `filter_category: { label, value, key }` object rather
  // than a plain `category` string — read whichever shape is present.
  const pageCategory =
    safeConfig.filter_category?.value ||
    safeConfig.filter_category?.key ||
    safeConfig.category;

  const title = safeConfig.title;

  /* ── CMS-driven filter & sort options (Strict: No uncontrolled fallbacks) ── */
  const allowFilter =
    Boolean(safeConfig.allow_filter) && safeConfig.allow_filter !== "false";
  const allowSort =
    Boolean(safeConfig.allow_sort) && safeConfig.allow_sort !== "false";

  const cmsDateOptions =
    !isArtistListing &&
    allowFilter &&
    safeConfig.allow_date_filter !== false &&
    Array.isArray(safeConfig.filter_date_options) &&
    safeConfig.filter_date_options.length > 0
      ? safeConfig.filter_date_options
      : [];

  const cmsCategoryOptions =
    allowFilter && safeConfig.allow_category_filter !== false
      ? isArtistListing
        ? Array.isArray(safeConfig.filter_genres) &&
          safeConfig.filter_genres.length > 0
          ? safeConfig.filter_genres
          : Array.isArray(safeConfig.filter_category_options) &&
              safeConfig.filter_category_options.length > 0
            ? safeConfig.filter_category_options
            : []
        : Array.isArray(safeConfig.filter_category_options) &&
            safeConfig.filter_category_options.length > 0
          ? safeConfig.filter_category_options
          : []
      : [];

  const cmsPriceOptions =
    !isArtistListing &&
    allowFilter &&
    safeConfig.allow_price_filter !== false &&
    Array.isArray(safeConfig.filter_price_options) &&
    safeConfig.filter_price_options.length > 0
      ? safeConfig.filter_price_options
      : [];

  const cmsSortOptions =
    allowSort &&
    Array.isArray(safeConfig.sort_options) &&
    safeConfig.sort_options.length > 0
      ? safeConfig.sort_options
      : [];

  const showDateFilter = cmsDateOptions.length > 0;
  const showCategoryFilter = cmsCategoryOptions.length > 0;
  const showPriceFilter = cmsPriceOptions.length > 0;
  const showSort = cmsSortOptions.length > 0;
  const showSidebar = showDateFilter || showCategoryFilter || showPriceFilter;

  const defaultSortValue =
    safeConfig.sort ||
    safeConfig.default_sort ||
    (showSort ? cmsSortOptions[0]?.value || "" : "");

  const categoryTabLabel = isArtistListing ? "Genres" : "Categories";

  const availableTabs = [
    showSort ? "sort" : null,
    showCategoryFilter ? (isArtistListing ? "genre" : "category") : null,
    showPriceFilter ? "price" : null,
    showDateFilter ? "date" : null,
  ].filter(Boolean);

  const showFilterButton = availableTabs.length > 0;

  /* ── state ── */
  const [data, setData] = useState(
    Array.isArray(initialData) ? initialData : [],
  );

  const [loading, setLoading] = useState(false);
  const [uiLoading, setUiLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [isFallback, setIsFallback] = useState(initialIsFallback);
  // Page 1 with default filters was fetched and rendered on the server
  // (components/Listing/ServerListing.js). Skip the mount-time fetch of the
  // same data — refetching would swap the rendered cards for skeletons and
  // back (a visible flash and layout shift) for nothing.
  const skipInitialFetchRef = useRef(
    Array.isArray(initialData) && initialData.length > 0,
  );

  const [dateFlt, setDateFlt] = useState([]);
  const [priceFlt, setPriceFlt] = useState([]);
  const [categoriesFlt, setCategoriesFlt] = useState([]);
  const [sortFlt, setSortFlt] = useState(defaultSortValue);

  // Artist listing search — looks up artists by name via the global search
  // API (the artist listing endpoint has no name-search filter of its own).
  const [artistSearchTerm, setArtistSearchTerm] = useState("");
  const hasSearchedRef = useRef(false);
  const latestArtistSearchRef = useRef("");

  // Organizer listing search — unlike artists, Organizer/get has its own
  // `search` param, so this just feeds straight into fetchData below
  // instead of needing a separate search endpoint/effect.
  const [organizerSearchTerm, setOrganizerSearchTerm] = useState("");
  const hasSearchedOrganizerRef = useRef(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  /* ── District-style Filter Modal state ── */
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(availableTabs[0] || "category");
  const [tempSort, setTempSort] = useState(sortFlt);
  const [tempDate, setTempDate] = useState(dateFlt);
  const [tempPrice, setTempPrice] = useState(priceFlt);
  const [tempCategories, setTempCategories] = useState(categoriesFlt);

  const handleOpenFilterModal = () => {
    setTempSort(sortFlt);
    setTempDate([...dateFlt]);
    setTempPrice([...priceFlt]);
    setTempCategories([...categoriesFlt]);
    const firstTab = availableTabs[0] || "category";
    setActiveTab(availableTabs.includes(activeTab) ? activeTab : firstTab);
    setFilterModalOpen(true);
  };

  const handleApplyModalFilters = () => {
    setSortFlt(tempSort);
    setDateFlt(tempDate);
    setPriceFlt(tempPrice);
    setCategoriesFlt(tempCategories);
    setFilterModalOpen(false);
    // Leave "popular events" fallback mode only if actual filter criteria
    // (date/price/category) are applied — not if only sort changed.
    const hasModalDirectFilters =
      tempDate.length > 0 || tempPrice.length > 0 || tempCategories.length > 0;
    if (isFallback && hasModalDirectFilters) {
      setIsFallback(false);
    }

    if (tempDate?.length || tempPrice?.length || tempCategories?.length) {
      trackFilterEvents({
        filter_type: [
          tempDate?.length ? "date" : null,
          tempPrice?.length ? "price" : null,
          tempCategories?.length ? "category" : null,
        ]
          .filter(Boolean)
          .join(","),
        filter_value: [
          ...(tempDate || []),
          ...(tempPrice || []),
          ...(tempCategories || []),
        ],
        city,
        category: pageCategory,
      });
    }
  };

  const handleClearModalFilters = () => {
    setTempSort("");
    setTempDate([]);
    setTempPrice([]);
    setTempCategories([]);
    setSortFlt("");
    setDateFlt([]);
    setPriceFlt([]);
    setCategoriesFlt([]);
    if (isFallback) {
      setIsFallback(false);
    }
    setFilterModalOpen(false);
  };

  const mainRef = useRef(null);

  /* ── fetch ── */
  const fetchData = useCallback(
    async (pageNum, currentFilters = {}, forceFallback = false) => {
      try {
        setUiLoading(true);
        setLoading(true);

        const baseEndpoint =
          endpoint ||
          (isThirdPartyListing
            ? ENDPOINTS.THIRD_PART_EVENT
            : isContestListing
              ? ENDPOINTS.GET_CONTESTS
              : isOrganizerListing
                ? ENDPOINTS.ORGANIZER_LIST
                : ENDPOINTS.HOME);

        // Organizer/get takes a flat `sort` param in its own vocabulary
        // (e.g. "latest"), not the events API's filters[sort_by] — keep it
        // out of the shared mappedSort/filters[sort_by] construction below,
        // and set it directly in the isOrganizerListing branch instead.
        const isFallbackFetch = forceFallback || isFallback;
        const mappedSort = isOrganizerListing
          ? ""
          : isArtistListing
            ? sortFlt || safeConfig.default_sort || "name_asc"
            : mapSortFilterValue(sortFlt);
        const params = {
          limit: safeConfig.limit || 12,
          page: pageNum,
          ...(!isFallbackFetch && mappedSort ? { "filters[sort_by]": mappedSort } : {}),
        };

        if (isArtistListing) {
          const { categories } = currentFilters;
          if (categories?.length) {
            params["filters[genre]"] = categories.join(",");
          }
        } else if (isOrganizerListing) {
          const term = organizerSearchTerm.trim();
          if (term) params.search = term;
          const organizerSort = sortFlt || safeConfig.default_sort;
          if (organizerSort) params.sort = organizerSort;
        } else if (!forceFallback && !isFallback) {
          params["filters[city]"] = city;
          if (isContestListing && city) {
            params["city"] = city;
          }
          if (safeConfig.filter_by && !isThirdPartyListing) {
            params["filters[tag]"] = safeConfig.filter_by;
          }

          const { dates, prices, categories } = currentFilters;
          // Backend expects filters[date] / filters[price] (mobile-compatible).
          const mappedDates = mapDateFilterValues(dates || []);
          const mappedPrices = mapPriceFilterValues(prices || []);
          if (mappedDates.length) {
            params["filters[date]"] = mappedDates.join(",");
          }
          if (mappedPrices.length) {
            params["filters[price]"] = mappedPrices.join(",");
          }

          // The sidebar "Categories" checkboxes must add to the page's own
          // category scope, not replace it — both used to write the same
          // filters[category_slug] key, so picking a checkbox silently
          // dropped the category the CMS page was scoped to.
          const allCategories = Array.from(
            new Set([
              ...(pageCategory ? [pageCategory] : []),
              ...(categories?.length ? categories : []),
            ]),
          );
          if (allCategories.length) {
            params["filters[category_slug]"] = allCategories.join(",");
          }
        }

        const { data: res, ok } = await api.get(baseEndpoint, params);

        if (ok) {
          const paginated = res?.data;
          const newData = Array.isArray(paginated?.data)
            ? paginated.data
            : Array.isArray(paginated)
              ? paginated
              : [];
          const { dates, prices, categories } = currentFilters;
          const hasActiveFilters =
            dates?.length > 0 || prices?.length > 0 || categories?.length > 0;

          // Category pages never fall back to "popular events": an empty
          // category should show as empty, scoped to its own category_slug,
          // rather than swapping in unrelated events from other cities.
          if (
            pageNum === 1 &&
            newData.length === 0 &&
            !pageCategory &&
            !isContestListing &&
            !isThirdPartyListing &&
            !isOrganizerListing &&
            !forceFallback &&
            !isFallback &&
            !hasActiveFilters
          ) {
            setIsFallback(true);
            fetchData(1, {}, true);
            return;
          }

          const finalData =
            isFallbackFetch && mappedSort
              ? sortEventsList(newData, mappedSort)
              : newData;

          setData(finalData);
          setTotalPages(paginated?.last_page || 1);
        }
      } catch (err) {
        console.error("Fetch Data Error:", err);
      } finally {
        setLoading(false);
        setUiLoading(false);
      }
    },
    [
      city,
      safeConfig.limit,
      safeConfig.default_sort,
      sortFlt,
      isFallback,
      pageCategory,
      safeConfig.filter_by,
      endpoint,
      type,
      isThirdPartyListing,
      isContestListing,
      isOrganizerListing,
      organizerSearchTerm,
    ],
  );

  useEffect(() => {
    // While an artist name search is active, filter/sort changes are queued
    // rather than applied — the search-results effect below owns `data`
    // until the search box is cleared.
    if (
      isArtistListing &&
      artistSearchTerm.trim().length >= ARTIST_SEARCH_MIN_LEN
    ) {
      return;
    }
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    setPage(1);
    fetchData(1, {
      dates: dateFlt,
      prices: priceFlt,
      categories: categoriesFlt,
    });
  }, [dateFlt, priceFlt, categoriesFlt, sortFlt]);

  /* ── artist name search (global search API, artist results only) ── */
  const searchArtists = useCallback(async (term) => {
    setUiLoading(true);
    setLoading(true);
    try {
      const url = `${getApiV2Url(ENDPOINTS.SEARCH)}?search=${encodeURIComponent(term)}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { ...DEV_AUTH_HEADERS },
      });
      const res = await response.json();

      if (term !== latestArtistSearchRef.current) return;

      const items = res?.status
        ? (res?.data || []).filter((item) => item.type === "artist")
        : [];
      setData(items);
      setTotalPages(1);
    } catch (err) {
      console.error("Artist search error:", err);
      setData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setUiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isArtistListing) return;
    const delay = setTimeout(() => {
      const trimmed = artistSearchTerm.trim();
      latestArtistSearchRef.current = trimmed;

      if (trimmed.length >= ARTIST_SEARCH_MIN_LEN) {
        hasSearchedRef.current = true;
        setPage(1);
        searchArtists(trimmed);
      } else if (trimmed.length === 0 && hasSearchedRef.current) {
        hasSearchedRef.current = false;
        setPage(1);
        fetchData(1, { categories: categoriesFlt });
      }
    }, 300);
    return () => clearTimeout(delay);
  }, [
    artistSearchTerm,
    isArtistListing,
    categoriesFlt,
    fetchData,
    searchArtists,
  ]);

  const isArtistSearchActive =
    isArtistListing && artistSearchTerm.trim().length >= ARTIST_SEARCH_MIN_LEN;

  /* ── organizer name search — Organizer/get's own `search` param ──
   * Mirrors the artist search effect's "only refetch on actual search
   * activity" guard so this doesn't double-fetch alongside the mount-time
   * fetch already triggered by the [dateFlt, priceFlt, categoriesFlt,
   * sortFlt] effect above. */
  useEffect(() => {
    if (!isOrganizerListing) return;
    const delay = setTimeout(() => {
      const trimmed = organizerSearchTerm.trim();
      if (trimmed) {
        hasSearchedOrganizerRef.current = true;
        setPage(1);
        fetchData(1, {});
      } else if (hasSearchedOrganizerRef.current) {
        hasSearchedOrganizerRef.current = false;
        setPage(1);
        fetchData(1, {});
      }
    }, 300);
    return () => clearTimeout(delay);
  }, [organizerSearchTerm, isOrganizerListing, fetchData]);

  // Artist and organizer listings both get a name-search box in the same
  // spot — share one set of bindings so the JSX below doesn't need to
  // branch per type.
  const isSearchableListing = isArtistListing || isOrganizerListing;
  const searchTerm = isOrganizerListing ? organizerSearchTerm : artistSearchTerm;
  const setSearchTerm = isOrganizerListing
    ? setOrganizerSearchTerm
    : setArtistSearchTerm;
  const searchPlaceholder = isOrganizerListing
    ? "Search organizers by name..."
    : "Search artists by name...";

  const handlePageChange = (newPage) => {
    if (loading || newPage < 1 || newPage > totalPages || newPage === page)
      return;
    setPage(newPage);
    fetchData(newPage, {
      dates: dateFlt,
      prices: priceFlt,
      categories: categoriesFlt,
    });
    mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* ── helpers ── */
  const toggleFilter = (val, kind) => {
    if (kind !== "sort" && isFallback) {
      setIsFallback(false);
    }
    if (kind === "sort") {
      setSortFlt("");
      setTempSort("");
      return;
    }
    const map = {
      date: [dateFlt, setDateFlt],
      price: [priceFlt, setPriceFlt],
      categories: [categoriesFlt, setCategoriesFlt],
    };
    const target = map[kind];
    if (!target) return;
    const [current, setter] = target;
    setter(
      current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val],
    );
  };

  const clearFilters = () => {
    if (isFallback) {
      setIsFallback(false);
    }
    setDateFlt([]);
    setPriceFlt([]);
    setCategoriesFlt([]);
    setSortFlt("");
    setTempSort("");
  };

  const getLabel = (val, options) => {
    const found = options.find((opt) =>
      typeof opt === "object" ? opt.value === val : opt === val,
    );
    if (!found) return val;
    return typeof found === "object" ? found.label : found;
  };

  const isSortApplied = Boolean(showSort && sortFlt);

  const allActiveTags = [
    ...(isSortApplied
      ? [
          {
            v: sortFlt,
            label: `Sort: ${getLabel(sortFlt, cmsSortOptions)}`,
            kind: "sort",
          },
        ]
      : []),
    ...(showDateFilter && cmsDateOptions.length > 0
      ? []
      : dateFlt.map((v) => ({
          v,
          label: getLabel(v, cmsDateOptions),
          kind: "date",
        }))),
    ...priceFlt.map((v) => ({
      v,
      label: getLabel(v, cmsPriceOptions),
      kind: "price",
    })),
    ...categoriesFlt.map((v) => ({
      v,
      label: getLabel(v, cmsCategoryOptions),
      kind: "categories",
    })),
  ];

  /* ── sidebar content (reused in both sidebar & drawer) ── */
  const SidebarContent = () => (
    <div className="flex gap-3 flex-col">
      {showDateFilter && (
        <FilterGroup
          label="Date"
          options={cmsDateOptions}
          active={dateFlt}
          onToggle={(v) => toggleFilter(v, "date")}
        />
      )}
      {showCategoryFilter && (
        <FilterGroup
          label={isArtistListing ? "Genres" : "Categories"}
          options={cmsCategoryOptions}
          active={categoriesFlt}
          onToggle={(v) => toggleFilter(v, "categories")}
        />
      )}
      {showPriceFilter && (
        <FilterGroup
          label="Price"
          options={cmsPriceOptions}
          active={priceFlt}
          onToggle={(v) => toggleFilter(v, "price")}
        />
      )}
    </div>
  );
  return (
    <section className={styles.section}>
      <Container>
        <div className={styles.layout}>
          {/* ── Main ── */}
          <div className={styles.main} ref={mainRef}>
            {/* Back button — mobile only */}
            <button
              type="button"
              onClick={() => router.back()}
              className="md:hidden flex items-center gap-1.5 text-[#021526] font-semibold text-xs border border-[#e3e6e9] rounded-lg px-2.5 py-1.5 bg-[#f3f6f9] mb-3 w-fit"
            >
              <IconArrowNarrowLeft size={15} />
              <span>Back</span>
            </button>

            {/* Top bar */}
            <div className={styles.topBar}>
              <div
                className={`${styles.titleBlock} ${isSearchableListing ? styles.titleBlockArtist : ""}`}
              >
                <h2 className={styles.pageTitle}>
                  {isFallback
                    ? "Popular Events"
                    : title ||
                      (isArtistListing
                        ? "Browse Artists"
                        : isOrganizerListing
                          ? "Browse Organizers"
                          : isContestListing
                            ? `Contests in ${formatCityName(city)}`
                            : isThirdPartyListing
                              ? `Events in ${formatCityName(city)}`
                              : type === "city"
                                ? `Events in ${formatCityName(city)}`
                                : category || "All Events")}
                </h2>
                {isFallback && (
                  <p className={styles.fallbackNote}>
                    No upcoming events found in {formatCityName(city)}. Showing
                    popular events from other cities.
                  </p>
                )}

                {/* Header Filter Bar */}
                {showFilterButton ||
                (showDateFilter && cmsDateOptions.length > 0) ? (
                  <div className={styles.filterBar} ref={setFilterBarRef}>
                    <div className={styles.stickyLeftFilters}>
                      {showFilterButton && (
                        <button
                          type="button"
                          className={`${styles.filterBtn} ${allActiveTags.length > 0 ? styles.filterBtnActive : ""}`}
                          onClick={handleOpenFilterModal}
                        >
                          <IconFilter size={16} />
                          <span>Filter</span>
                          {allActiveTags.length > 0 && (
                            <span className={styles.filterBadge}>
                              {allActiveTags.length}
                            </span>
                          )}
                          <IconChevronDown size={14} />
                        </button>
                      )}
                      {(sortFlt || dateFlt.length > 0 || priceFlt.length > 0 || categoriesFlt.length > 0) && (
                        <button
                          type="button"
                          className={styles.clearAllBtn}
                          onClick={clearFilters}
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    {showDateFilter && cmsDateOptions.length > 0 && (
                      <div className={styles.quickDatePills}>
                        {cmsDateOptions.map((item) => {
                          const val =
                            typeof item === "object" ? item.value : item;
                          const label =
                            typeof item === "object" ? item.label : item;
                          const isActive = dateFlt.includes(val);
                          return (
                            <button
                              key={val}
                              type="button"
                              className={`${styles.datePill} ${isActive ? styles.datePillActive : ""}`}
                              onClick={() => toggleFilter(val, "date")}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Active filter tags moved here */}
                    {(allActiveTags.length > 0 || dateFlt.length > 0) && (
                      <>
                        {allActiveTags.map((tag) => (
                          <span
                            className={styles.activeTag}
                            key={`${tag.kind}-${tag.v}`}
                          >
                            <span>{tag.label}</span>
                            <button
                              type="button"
                              className={styles.activeTagClose}
                              onClick={() => toggleFilter(tag.v, tag.kind)}
                              aria-label={`Remove filter ${tag.label}`}
                            >
                              <IconX size={12} />
                            </button>
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                ) : (
                  isSearchableListing && (
                    <div
                      className={styles.artistSearchBar}
                      style={{ margin: 0 }}
                    >
                      <IconSearch
                        size={17}
                        className={styles.artistSearchIcon}
                      />
                      <input
                        type="text"
                        className={styles.artistSearchInput}
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm ? (
                        <button
                          type="button"
                          className={styles.artistSearchClear}
                          onClick={() => setSearchTerm("")}
                          aria-label="Clear search"
                        >
                          <IconX size={14} />
                        </button>
                      ) : null}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Name search when filters ARE available (shown next to them) */}
            {isSearchableListing &&
              (showFilterButton ||
                (showDateFilter && cmsDateOptions.length > 0)) && (
                <div className={styles.artistSearchBar}>
                  <IconSearch size={17} className={styles.artistSearchIcon} />
                  <input
                    type="text"
                    className={styles.artistSearchInput}
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      className={styles.artistSearchClear}
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                    >
                      <IconX size={14} />
                    </button>
                  ) : null}
                </div>
              )}

            {/* Active filter tags have been moved into the top filterBar */}

            {/* Grid */}
            <div
              className={
                isOrganizerListing
                  ? styles.organizerGrid
                  : isArtistListing
                    ? styles.artistGrid
                    : styles.grid
              }
            >
              {uiLoading ? (
                Array.from({
                  length: isArtistListing || isOrganizerListing ? 10 : 6,
                }).map((_, i) => <SkeletonCard key={i} />)
              ) : data.length === 0 ? (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>
                    {isArtistListing
                      ? "🎤"
                      : isOrganizerListing
                        ? "🏢"
                        : isContestListing
                          ? "🏆"
                          : "🗓️"}
                  </span>
                  <p className={styles.emptyText}>
                    {isArtistListing
                      ? isArtistSearchActive
                        ? `No artists found for "${artistSearchTerm.trim()}".`
                        : "No artists found. Try adjusting your filters."
                      : isOrganizerListing
                        ? organizerSearchTerm.trim()
                          ? `No organizers found for "${organizerSearchTerm.trim()}".`
                          : "No organizers found."
                        : isContestListing
                          ? `No contests found in ${formatCityName(city)}.`
                          : isThirdPartyListing
                            ? `No third-party events found in ${formatCityName(city)}.`
                            : "No events found. Try adjusting your filters."}
                  </p>
                </div>
              ) : (
                data.map((item, i) =>
                  renderCard ? (
                    renderCard(item, i)
                  ) : isOrganizerListing ? (
                    <OrganizerListingCard
                      key={item.id ?? item.workspace_slug ?? i}
                      organizer={item}
                    />
                  ) : isArtistListing ? (
                    <ArtistCard
                      key={item.id ?? item.slug ?? i}
                      title={
                        `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
                        item.name ||
                        "Artist"
                      }
                      name={
                        `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
                        item.name ||
                        "Artist"
                      }
                      img={pickArtistImage(
                        item.profile_pic?.[0] || item.profile_pic || item.image,
                      )}
                      slug={item.slug}
                      genres={item.genres}
                      gender={item.gender}
                    />
                  ) : isContestListing ? (
                    <ContestCard
                      key={item.id ?? item.slug ?? i}
                      slug={item.slug}
                      title={item.title}
                      description={item.description}
                      banner={item.banner}
                      end_at={item.end_at}
                      status={item.status}
                      participants_count={item.participants_count}
                      city={item.city || city}
                    />
                  ) : (
                    <Event
                      key={item.id ?? item.slug ?? i}
                      id={item.id}
                      name={item.name}
                      slug={item.slug}
                      start_time={item.start_time}
                      start_date={item.start_date}
                      yet_to_be_announced={item.yet_to_be_announced}
                      venue={item.venue}
                      image={item.image}
                      price={item.price}
                      timezone={item.timezone}
                      amount={item.amount}
                      currency={item.currency}
                      end_date={item.end_date}
                      end_time={item.end_time}
                      sold_out={item.sold_out}
                      max_price={item.max_price}
                      organizer={item.organizer}
                      status={item.status}
                      is_contest={item.is_contest}
                      description={item.description}
                      is_third_party={item.is_third_party}
                      source_platform_logo={item.source_platform_logo}
                    />
                  ),
                )
              )}
            </div>

            {/* Pagination */}
            {!uiLoading && data.length > 0 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                disabled={loading}
                onChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </Container>

      {/* ── Mobile Drawer ── */}
      {showSidebar && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          height="85vh"
          closable={false}
          className={styles.mobileDrawer}
          rootClassName={styles.mobileDrawerRoot}
        >
          <div className={styles.drawerHandle} />

          <div className={styles.drawerHeader}>
            <h4 className={styles.drawerTitle}>Filters</h4>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {allActiveTags.length > 0 && (
                <button
                  className={styles.drawerClearBtn}
                  onClick={clearFilters}
                >
                  Clear All
                  {/* <IconX size={16} /> */}
                </button>
              )}
              <button
                className={styles.drawerCloseBtn}
                onClick={() => setDrawerOpen(false)}
              >
                <IconX size={22} />
              </button>
            </div>
          </div>

          <div>
            <SidebarContent />
          </div>

          {/* <Button
            style={{
              width: "100%",
              height: "44px",
              borderRadius: "60px",
              marginTop: "24px",
            }}
            type="default"
            onClick={() => setDrawerOpen(false)}
          >
            Apply Filters
          </Button> */}
        </Drawer>
      )}

      {/* ── District-style Filter Modal ── */}
      <Modal
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        footer={null}
        closeIcon={<IconX size={18} color="#000" />}
        centered
        width={560}
        className={styles.filterModal}
        rootClassName={styles.filterModalRoot}
        destroyOnHidden
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Filter by</h3>
        </div>

        <div className={styles.modalBody}>
          {/* Left Tabs */}
          <div className={styles.modalTabs}>
            {showSort && (
              <button
                type="button"
                className={`${styles.modalTabBtn} ${activeTab === "sort" ? styles.modalTabBtnActive : ""}`}
                onClick={() => setActiveTab("sort")}
              >
                Sort By
              </button>
            )}
            {showCategoryFilter && (
              <button
                type="button"
                className={`${styles.modalTabBtn} ${activeTab === "category" || activeTab === "genre" ? styles.modalTabBtnActive : ""}`}
                onClick={() =>
                  setActiveTab(isArtistListing ? "genre" : "category")
                }
              >
                {categoryTabLabel}
              </button>
            )}
            {showPriceFilter && (
              <button
                type="button"
                className={`${styles.modalTabBtn} ${activeTab === "price" ? styles.modalTabBtnActive : ""}`}
                onClick={() => setActiveTab("price")}
              >
                Price
              </button>
            )}
            {showDateFilter && (
              <button
                type="button"
                className={`${styles.modalTabBtn} ${activeTab === "date" ? styles.modalTabBtnActive : ""}`}
                onClick={() => setActiveTab("date")}
              >
                Date
              </button>
            )}
          </div>

          {/* Right Content */}
          <div className={styles.modalContent}>
            {showSort && activeTab === "sort" && (
              <div className={styles.modalOptionsList}>
                {cmsSortOptions.map((opt) => {
                  const val = typeof opt === "object" ? opt.value : opt;
                  const label = typeof opt === "object" ? opt.label : opt;
                  const isSelected = Boolean(tempSort) && tempSort === val;
                  return (
                    <div
                      key={val}
                      className={styles.modalRadioItem}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setTempSort((prev) => (prev === val ? "" : val))
                      }
                    >
                      <div
                        className={`${styles.radioCircle} ${isSelected ? styles.radioCircleSelected : ""}`}
                      >
                        {isSelected && <div className={styles.radioDot} />}
                      </div>
                      <span className={styles.optionText}>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {showCategoryFilter &&
              (activeTab === "category" || activeTab === "genre") && (
                <div className={styles.modalOptionsList}>
                  {cmsCategoryOptions.map((opt) => {
                    const val = typeof opt === "object" ? opt.value : opt;
                    const label = typeof opt === "object" ? opt.label : opt;
                    const isChecked = tempCategories.includes(val);
                    return (
                      <div
                        key={val}
                        className={styles.modalCheckboxItem}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setTempCategories((prev) =>
                            prev.includes(val)
                              ? prev.filter((c) => c !== val)
                              : [...prev, val],
                          );
                        }}
                      >
                        <Checkbox checked={isChecked} />
                        <span className={styles.optionText}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

            {showPriceFilter && activeTab === "price" && (
              <div className={styles.modalOptionsList}>
                {cmsPriceOptions.map((opt) => {
                  const val = typeof opt === "object" ? opt.value : opt;
                  const label = typeof opt === "object" ? opt.label : opt;
                  const isChecked = tempPrice.includes(val);
                  return (
                    <div
                      key={val}
                      className={styles.modalCheckboxItem}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setTempPrice((prev) =>
                          prev.includes(val)
                            ? prev.filter((p) => p !== val)
                            : [...prev, val],
                        );
                      }}
                    >
                      <Checkbox checked={isChecked} />
                      <span className={styles.optionText}>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {showDateFilter && activeTab === "date" && (
              <div className={styles.modalOptionsList}>
                {cmsDateOptions.map((opt) => {
                  const val = typeof opt === "object" ? opt.value : opt;
                  const label = typeof opt === "object" ? opt.label : opt;
                  const isChecked = tempDate.includes(val);
                  return (
                    <div
                      key={val}
                      className={styles.modalCheckboxItem}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setTempDate((prev) =>
                          prev.includes(val)
                            ? prev.filter((d) => d !== val)
                            : [...prev, val],
                        );
                      }}
                    >
                      <Checkbox checked={isChecked} />
                      <span className={styles.optionText}>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.modalClearBtn}
            onClick={handleClearModalFilters}
          >
            Clear filters
          </button>
          <button
            type="button"
            className={styles.modalApplyBtn}
            onClick={handleApplyModalFilters}
          >
            Apply Filters
          </button>
        </div>
      </Modal>
    </section>
  );
}
